import { BRAND, UI } from '../theme/kioskTheme';
import logoForvet from '../assets/logo-forvet-dark.svg';

/**
 * Hojas que salen de la impresora del stand.
 *
 * Dos caminos a propósito, porque las impresoras de evento se dividen en dos
 * mundos y no sabemos cuál va a haber:
 *
 *   - imprimir()  → diálogo de impresión del navegador. Sirve con AirPrint,
 *                   Mopria y cualquier impresora que el sistema ya conozca.
 *   - compartir() → menú de compartir del sistema con el PNG adjunto, para
 *                   mandarlo a la app de una Instax Link / Sprocket, que el
 *                   navegador no ve. Si tampoco hay Web Share, descarga.
 *
 * Las dos salen de la MISMA imagen compuesta, así que lo que se ve en la
 * pantalla es exactamente lo que sale por la impresora.
 *
 * 1200×1800 es 2:3: entra sin recorte tanto en 10×15 cm como en las de bolsillo
 * de 5×7,6 cm, que es el formato de casi todas las divertidas.
 */
const ANCHO = 1200;
const ALTO = 1800;
const TIPO = 'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
const MONO = 'ui-monospace, Menlo, Consolas, "Courier New", monospace';

function lienzo() {
  const c = document.createElement('canvas');
  c.width = ANCHO;
  c.height = ALTO;
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, ANCHO, ALTO);
  ctx.textBaseline = 'top';
  return { c, ctx };
}

const aBlob = c => new Promise(res => c.toBlob(res, 'image/png'));

function cargarImagen(src) {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });
}

/** Texto centrado con corte por palabras. Devuelve el y de abajo de todo. */
function parrafo(ctx, texto, { y, tam, peso = '400', color, alto = 1.22, ancho = ANCHO - 160, familia = TIPO }) {
  ctx.font = `${peso} ${tam}px ${familia}`;
  ctx.fillStyle = color;
  ctx.textAlign = 'center';

  const palabras = String(texto || '').split(/\s+/).filter(Boolean);
  const lineas = [];
  let actual = '';
  for (const p of palabras) {
    const prueba = actual ? `${actual} ${p}` : p;
    if (ctx.measureText(prueba).width > ancho && actual) {
      lineas.push(actual);
      actual = p;
    } else {
      actual = prueba;
    }
  }
  if (actual) lineas.push(actual);

  lineas.forEach((l, i) => ctx.fillText(l, ANCHO / 2, y + i * tam * alto));
  return y + lineas.length * tam * alto;
}

/**
 * Pie de marca. El logo es un SVG y dibujarlo en canvas falla en algunos
 * navegadores viejos sin avisar, así que si no carga cae en tipografía en vez
 * de dejar el pie vacío.
 */
async function pie(ctx, texto = 'CVDL 2026') {
  const altoPie = 150;
  const y = ALTO - altoPie;
  ctx.fillStyle = BRAND.forvetNavy;
  ctx.fillRect(0, y, ANCHO, altoPie);

  const centro = y + altoPie / 2;
  try {
    const logo = await cargarImagen(logoForvet);
    const h = 46;
    const w = (logo.width / logo.height) * h;
    // El SVG es de tinta oscura: sobre el navy va invertido.
    const off = document.createElement('canvas');
    off.width = w; off.height = h;
    const octx = off.getContext('2d');
    octx.drawImage(logo, 0, 0, w, h);
    octx.globalCompositeOperation = 'source-in';
    octx.fillStyle = '#FFFFFF';
    octx.fillRect(0, 0, w, h);
    ctx.drawImage(off, 90, centro - h / 2);
  } catch {
    ctx.textAlign = 'left';
    ctx.font = `800 44px ${TIPO}`;
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText('FORVET', 90, centro - 26);
  }

  ctx.textAlign = 'right';
  ctx.font = `600 34px ${TIPO}`;
  ctx.fillStyle = 'rgba(255,255,255,.85)';
  ctx.fillText(texto, ANCHO - 90, centro - 20);
}

/** Dibuja la foto recortada al cuadrado, sin deformarla. */
function fotoCuadrada(ctx, img, x, y, lado) {
  const escala = Math.max(lado / img.width, lado / img.height);
  const w = img.width * escala;
  const h = img.height * escala;
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, lado, lado);
  ctx.clip();
  ctx.drawImage(img, x + (lado - w) / 2, y + (lado - h) / 2, w, h);
  ctx.restore();
}

/**
 * La foto de la caravana con el número grande: es la que va al muro.
 * Recibe el blob que acaba de sacar la cámara, no la URL de S3 — así no
 * dependemos de que el bucket tenga CORS ni de una segunda descarga.
 */
export async function componerFotoCaravana({ blob, numero, nombre }) {
  const { c, ctx } = lienzo();
  const url = URL.createObjectURL(blob);
  try {
    const img = await cargarImagen(url);
    const lado = ANCHO - 160;
    fotoCuadrada(ctx, img, 80, 90, lado);

    let y = 90 + lado + 56;
    ctx.textAlign = 'center';
    ctx.font = `600 40px ${TIPO}`;
    ctx.fillStyle = UI.tintaSuave;
    ctx.fillText('CARAVANA', ANCHO / 2, y);

    y += 54;
    ctx.font = `900 200px ${MONO}`;
    ctx.fillStyle = BRAND.forvetNavy;
    ctx.fillText(String(numero).padStart(3, '0'), ANCHO / 2, y);

    y += 236;
    parrafo(ctx, nombre ? `${nombre} está buscando a su gemelo` : 'Buscá a la persona con tu mismo número',
      { y, tam: 40, peso: '600', color: UI.tinta });

    await pie(ctx);
    return aBlob(c);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** El premio de la ruleta con el código de canje. */
export async function componerPremio({ nombre, titulo, detalle, codigo, instrucciones, vence }) {
  const { c, ctx } = lienzo();

  ctx.fillStyle = BRAND.forvetNavy;
  ctx.fillRect(0, 0, ANCHO, 190);
  ctx.textAlign = 'center';
  ctx.font = `800 52px ${TIPO}`;
  ctx.fillStyle = '#FFFFFF';
  ctx.fillText(nombre ? `¡GANASTE, ${nombre}!` : '¡GANASTE!', ANCHO / 2, 66);

  let y = 270;
  y = parrafo(ctx, titulo, { y, tam: 76, peso: '900', color: BRAND.forvetNavy, alto: 1.14 });

  if (detalle && detalle !== titulo) {
    y = parrafo(ctx, detalle, { y: y + 24, tam: 40, peso: '400', color: UI.tintaSuave });
  }

  if (codigo) {
    y += 60;
    const alto = 250;
    ctx.strokeStyle = UI.accion;
    ctx.lineWidth = 6;
    ctx.strokeRect(80, y, ANCHO - 160, alto);

    ctx.textAlign = 'center';
    ctx.font = `600 34px ${TIPO}`;
    ctx.fillStyle = UI.tintaSuave;
    ctx.fillText('TU CÓDIGO', ANCHO / 2, y + 34);

    // Sin I, O, 0 ni 1 en el alfabeto: se puede dictar en voz alta sin dudas.
    ctx.font = `900 92px ${MONO}`;
    ctx.fillStyle = BRAND.forvetNavy;
    ctx.fillText(codigo, ANCHO / 2, y + 96);

    if (vence) {
      ctx.font = `600 32px ${TIPO}`;
      ctx.fillStyle = UI.tintaSuave;
      ctx.fillText(`Vence el ${vence}`, ANCHO / 2, y + 200);
    }
    y += alto;
  }

  if (instrucciones) {
    parrafo(ctx, instrucciones, { y: y + 60, tam: 34, peso: '400', color: UI.tinta, ancho: ANCHO - 220 });
  }

  await pie(ctx);
  return aBlob(c);
}

/** Diálogo de impresión del sistema con la hoja sola, sin nada de la app. */
export async function imprimir(blob, titulo = 'FORVET') {
  const url = URL.createObjectURL(blob);
  const marco = document.createElement('iframe');
  marco.setAttribute('aria-hidden', 'true');
  marco.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  document.body.appendChild(marco);

  const doc = marco.contentWindow.document;
  doc.open();
  doc.write(
    `<!doctype html><html><head><title>${titulo}</title><style>` +
    '@page{margin:0}html,body{margin:0;height:100%}' +
    'img{width:100%;height:100%;object-fit:contain;display:block}' +
    `</style></head><body><img src="${url}" alt=""></body></html>`,
  );
  doc.close();

  await new Promise(res => {
    const img = doc.images[0];
    if (img?.complete) res();
    else { img.onload = res; img.onerror = res; }
  });

  marco.contentWindow.focus();
  marco.contentWindow.print();

  // El diálogo es modal y no avisa cuándo se cerró: se limpia con holgura.
  setTimeout(() => { marco.remove(); URL.revokeObjectURL(url); }, 120000);
}

/**
 * Menú de compartir del sistema con el PNG adjunto: es la única vía para las
 * impresoras Bluetooth con app propia.
 *
 * Ojo: navigator.share exige un gesto del usuario, así que el blob tiene que
 * estar compuesto ANTES del clic. Por eso las pantallas lo arman apenas
 * tienen los datos y no cuando se toca el botón.
 */
export async function compartir(blob, nombreArchivo = 'forvet.png') {
  const archivo = new File([blob], nombreArchivo, { type: 'image/png' });

  if (navigator.canShare?.({ files: [archivo] })) {
    try {
      await navigator.share({ files: [archivo], title: 'FORVET · CVDL 2026' });
      return 'compartido';
    } catch (err) {
      if (err?.name === 'AbortError') return 'cancelado';
      // Si el sistema lo rechaza, todavía queda la descarga.
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombreArchivo;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 20000);
  return 'descargado';
}
